import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  useToast,
  Spinner,
  Flex,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { getPlanDisplayName, getPlanPrice, getAllPlans, PLAN_ESSENTIAL, getOrderLimit } from "../../utils/subscription/planFeatures";
import { Store } from "../type/Store";
import { initializeStripeProducts } from "../../utils/stripe/stripeProducts";
import { useStore } from "../contexts/StoreContext";
import PageShell from "../layout/PageShell";

const SuperAdminSubscriptionDashboard: React.FC = () => {
  const toast = useToast();
  const { allStores } = useStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPlan, setFilterPlan] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [initializingProducts, setInitializingProducts] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [updatingPlan, setUpdatingPlan] = useState(false);

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "white");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  const filteredStores = useMemo(() => {
    return allStores.filter((store) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !store.name?.toLowerCase().includes(query) &&
          !store.email?.toLowerCase().includes(query) &&
          !store.id?.toLowerCase().includes(query)
        ) {
          return false;
        }
      }

      // Plan filter
      if (filterPlan !== "all") {
        if (store.subscriptionPlan !== filterPlan) {
          return false;
        }
      }

      // Status filter
      if (filterStatus !== "all") {
        if (store.subscriptionStatus !== filterStatus) {
          return false;
        }
      }

      return true;
    });
  }, [allStores, searchQuery, filterPlan, filterStatus]);

  const handleManageStore = (store: Store) => {
    setSelectedStore(store);
    setSelectedPlan(store.subscriptionPlan || PLAN_ESSENTIAL);
    onOpen();
  };

  const handleUpdatePlan = async () => {
    if (!selectedStore || !selectedPlan) return;

    setUpdatingPlan(true);
    try {
      const storeRef = doc(db, "stores", selectedStore.id);
      const orderLimit = getOrderLimit(selectedPlan);
      
      await updateDoc(storeRef, {
        subscriptionPlan: selectedPlan,
        subscriptionStatus: "active",
        orderLimit: orderLimit,
        updated: serverTimestamp(),
      });

      toast({
        title: "Success",
        description: `Plan updated to ${getPlanDisplayName(selectedPlan)}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();
      setSelectedStore(null);
      setSelectedPlan("");
    } catch (error: any) {
      console.error("Error updating plan:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update plan",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingPlan(false);
    }
  };

  const handleInitializeProducts = async () => {
    // Use any store ID or "global" - Stripe products are global, not per-store
    // The storeId is only used to get the Stripe client, which now reads from global config
    const storeIdForStripe = allStores.length > 0 ? allStores[0].id : "global";

    setInitializingProducts(true);
    try {
      await initializeStripeProducts(storeIdForStripe);
      toast({
        title: "Success",
        description: "Stripe products initialized successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error initializing Stripe products:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to initialize Stripe products",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setInitializingProducts(false);
    }
  };

  return (
    <PageShell
      title="Subscriptions"
      description="Assign a plan to any store."
      wide
      actions={
        <Button
          onClick={handleInitializeProducts}
          isLoading={initializingProducts}
          loadingText="Initializing..."
          isDisabled={allStores.length === 0}
        >
          Initialize Stripe Products
        </Button>
      }
    >
      <VStack spacing={6} align="stretch">

        <HStack spacing={4}>
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg={bgCard}
            />
          </InputGroup>

          <Select
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
            maxW="200px"
            bg={bgCard}
          >
            <option value="all">All Plans</option>
            <option value="essential">Essential</option>
            <option value="standard">Standard</option>
            <option value="pro_kitchen">Pro Kitchen</option>
            <option value="elite_kitchen">Elite Kitchen</option>
          </Select>

          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            maxW="200px"
            bg={bgCard}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="canceled">Canceled</option>
            <option value="past_due">Past Due</option>
            <option value="trialing">Trialing</option>
          </Select>
        </HStack>

        <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor}>
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Store Name</Th>
                  <Th>Current Plan</Th>
                  <Th>Status</Th>
                  <Th>Price</Th>
                  <Th>Store Admin</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredStores.map((store) => (
                  <Tr key={store.id}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold">{store.name}</Text>
                        <Text fontSize="xs" color={textSecondary}>
                          {store.id}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Text>{getPlanDisplayName(store.subscriptionPlan)}</Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          store.subscriptionStatus === "active"
                            ? "green"
                            : store.subscriptionStatus === "past_due"
                            ? "yellow"
                            : store.subscriptionStatus === "canceled"
                            ? "red"
                            : "gray"
                        }
                      >
                        {store.subscriptionStatus || "active"}
                      </Badge>
                    </Td>
                    <Td>
                      <Text>
                        ${getPlanPrice(store.subscriptionPlan)}
                        {getPlanPrice(store.subscriptionPlan) > 0 && "/mo"}
                      </Text>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        {store.admins && store.admins.length > 0 ? (
                          store.admins.map((admin, idx) => (
                            <Text key={idx} fontSize="sm">
                              {admin}
                            </Text>
                          ))
                        ) : (
                          <Text fontSize="sm" color={textSecondary}>
                            {store.createdBy || "N/A"}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => handleManageStore(store)}
                      >
                        Manage
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {filteredStores.length === 0 && (
            <Flex justify="center" align="center" py={8}>
              <Text color={textSecondary}>No stores found</Text>
            </Flex>
          )}
        </Box>

        <HStack>
          <Text fontSize="sm" color={textSecondary}>
            Total Stores: {allStores.length}
          </Text>
          <Text fontSize="sm" color={textSecondary}>
            Filtered: {filteredStores.length}
          </Text>
        </HStack>
      </VStack>

      {/* Plan Assignment Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Assign Plan - {selectedStore?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Current Plan</FormLabel>
                <Text fontSize="sm" color={textSecondary}>
                  {selectedStore ? getPlanDisplayName(selectedStore.subscriptionPlan) : ""}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Select Plan</FormLabel>
                <Select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  bg={bgCard}
                >
                  {getAllPlans().map((planId) => (
                    <option key={planId} value={planId}>
                      {getPlanDisplayName(planId)} - ${getPlanPrice(planId)}/mo
                    </option>
                  ))}
                </Select>
              </FormControl>

              {selectedPlan && (
                <Box p={4} bg={bgCard} borderRadius="md" border="1px" borderColor={borderColor}>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold">
                      Plan Details
                    </Text>
                    <Text fontSize="sm">
                      <strong>Price:</strong> ${getPlanPrice(selectedPlan)}/mo
                    </Text>
                    <Text fontSize="sm">
                      <strong>Order Limit:</strong>{" "}
                      {getOrderLimit(selectedPlan) === null
                        ? "Unlimited"
                        : `${getOrderLimit(selectedPlan)} per day`}
                    </Text>
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdatePlan}
              isLoading={updatingPlan}
              loadingText="Updating..."
            >
              Update Plan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
};

export default SuperAdminSubscriptionDashboard;

